<template>
  <div class="p-contact-wizard-step">
    <div class="p-contact-wizard-step__card">
      <card
        :title="title"
        :description="$t('wizard.contacts.description')"
      >
        <div class="p-contact-wizard-step-card">
          <div
            class="p-contact-wizard-step-card__description"
            data-test-id="contact-wizard-step.description"
          >
            <div class="el-text el-text--body-24 el-text--white-space-pre-line">
              {{ $t('wizard.contacts.form.description') }}
            </div>
          </div>
          <div
            v-for="form in forms"
            :key="form.contactType"
            class="p-contact-wizard-step-card__form"
          >
            <div class="p-contact-wizard-step-form">
              <div
                class="p-contact-wizard-step-form__title el-text el-text--paragraph-accent"
                :class="[
                  form.title ?`qa-text-${qaFormTitle(form.title)}`: '',
                ]"
                :data-test-id="`contact-wizard-step.form-title-${qaFormTitle(form.title)}`"
              >
                {{ form.title }}
              </div>
              <div
                class="p-contact-wizard-step-form__description el-text el-text--body-24"
                :data-test-id="`contact-wizard-step.form-title-${qaFormTitle(form.title)}.description`"
              >
                {{ form.description }}
              </div>
              <div class="p-contact-wizard-step-form__content">
                <component
                  :is="form.component.is"
                  :ref="form.contactType"
                  v-bind="form.component.props"
                  v-on="form.component.listeners"
                />
              </div>
              <div
                v-if="isBillingForm(form.contactType)"
                class="p-contact-wizard-step-form__footer"
              >
                <div>
                  <el-checkbox
                    v-model="isBusinessChecked"
                    :disabled="!isBillingFormFilled"
                    data-test-id="contact-wizard-step.checkbox-business-contact"
                  >
                    {{ $t('wizard.contacts.form.billing.as-business') }}
                  </el-checkbox>
                </div>
                <div>
                  <el-checkbox
                    v-model="isTechnicalChecked"
                    :disabled="!isBillingFormFilled"
                    data-test-id="contact-wizard-step.checkbox-technical-contact"
                  >
                    {{ $t('wizard.contacts.form.billing.as-technical') }}
                  </el-checkbox>
                </div>
              </div>
            </div>
          </div>
        </div>
      </card>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { ContactType, hasContactType, resetContactType, areContactRequiredFieldsFilled } from '@/app/domain/Contact';
import { setAutoFocus } from '@/app/library/setAutoFocus';
import Card from '@/app/components/Card';
import ContactForm from '@/app/components/ContactForm';

export default {
  name: 'ContactsWizardStep',
  components: {
    Card,
    ContactForm
  },
  props: {
    value: {
      type: Array,
      default: () => []
    },
    stepIndex: {
      type: Number,
      required: true
    }
  },
  emits: [
    'input'
  ],
  data() {
    return {
      forms: [],
      isBillingFormFilled: false,
      isBusinessChecked: false,
      isTechnicalChecked: false
    };
  },
  computed: {
    ...mapGetters('app', [
      'contactList'
    ]),
    title() {
      return `${this.stepIndex}. ${this.$t('wizard.contacts.title')}`;
    }
  },
  watch: {
    isBillingFormFilled(value) {
      if (!value) {
        this.isBusinessChecked = false;
        this.isTechnicalChecked = false;
      }
    },
    isBusinessChecked(value) {
      const businessForm = this.forms.find((form) => this.isBusinessForm(form.contactType));
      businessForm.component.props.isDisabled = value;
      this.emitFormValue();
    },
    isTechnicalChecked(value) {
      const technicalForm = this.forms.find((form) => this.isTechnicalForm(form.contactType));
      technicalForm.component.props.isDisabled = value;
      this.emitFormValue();
    }
  },
  created() {
    this.forms = [
      {
        contactType: ContactType.Billing,
        title: this.$t('wizard.contacts.form.billing.title'),
        description: this.$t('wizard.contacts.form.billing.description'),
        component: {
          is: ContactForm,
          props: {
            value: this.getDefaultFormValue(),
            contactList: this.contactList,
            isDisabled: false,
            showConsentText: false
          },
          listeners: {
            input: (value) => this.handleFormValueChange(ContactType.Billing, value)
          }
        }
      },
      {
        contactType: ContactType.Business,
        title: this.$t('wizard.contacts.form.business.title'),
        description: this.$t('wizard.contacts.form.business.description'),
        component: {
          is: ContactForm,
          props: {
            value: this.getDefaultFormValue(),
            contactList: this.contactList,
            isDisabled: false,
            showConsentText: false
          },
          listeners: {
            input: (value) => this.handleFormValueChange(ContactType.Business, value)
          }
        }
      },
      {
        contactType: ContactType.Technical,
        title: this.$t('wizard.contacts.form.technical.title'),
        description: this.$t('wizard.contacts.form.technical.description'),
        component: {
          is: ContactForm,
          props: {
            value: this.getDefaultFormValue(),
            contactList: this.contactList,
            isDisabled: false,
            showConsentText: false
          },
          listeners: {
            input: (value) => this.handleFormValueChange(ContactType.Technical, value)
          }
        }
      }
    ];
    this.setFormValues(this.value);
  },
  async mounted() {
    await this.$nextTick();
    setAutoFocus(this.$el);
  },
  methods: {
    handleFormValueChange(contactType, value) {
      if (contactType === ContactType.Billing) {
        this.isBillingFormFilled = areContactRequiredFieldsFilled(value);
      }
      const form = this.getFormByContactType(contactType);
      if (form) {
        form.component.props.value = value;
      }
      this.emitFormValue();
    },
    emitFormValue() {
      this.$emit('input', this.collapseContacts(this.filterContacts(this.getFormValues())));
    },
    getDefaultFormValue() {
      return {
        id: '',
        type: [],
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        jobTitle: ''
      };
    },
    getFormValues() {
      return this.forms.map((form) => this.addContactType(form.component.props.value, form.contactType));
    },
    setFormValues(values) {
      values.forEach((value) => {
        value.type.forEach((type) => {
          const form = this.getFormByContactType(type);

          if (form) {
            form.component.props.value = resetContactType(value);
          }
        });
      });
    },
    getFormByContactType(contactType) {
      return this.forms.find((form) => form.contactType === contactType);
    },
    addContactType(contact, contactType) {
      return {
        ...contact,
        type: [...contact.type, contactType]
      };
    },
    filterContacts(contacts) {
      const [billingFormValue, ...otherFormValues] = contacts;

      return otherFormValues.reduce((result, formValue) => {
        if (hasContactType(formValue, ContactType.Business) && this.isBusinessChecked) {
          result[0].type = result[0].type.concat(formValue.type);
        } else if (hasContactType(formValue, ContactType.Technical) && this.isTechnicalChecked) {
          result[0].type = result[0].type.concat(formValue.type);
        } else {
          result.push(formValue);
        }
        return result;
      }, [billingFormValue]);
    },
    collapseContacts(contacts) {
      return contacts.reduce((result, contact) => {
        const foundContact = result.find((c) => (
          c.id === contact.id
          && c.firstName === contact.firstName
          && c.lastName === contact.lastName
          && c.email === contact.email
          && c.phone === contact.phone
          && c.jobTitle === contact.jobTitle
        ));

        if (foundContact) {
          foundContact.type = Array.from(new Set([
            ...foundContact.type,
            ...contact.type
          ]));
        } else {
          result.push({
            ...contact,
            type: [...contact.type]
          });
        }
        return result;
      }, []);
    },
    isBillingForm(contactType) {
      return contactType === ContactType.Billing;
    },
    isBusinessForm(contactType) {
      return contactType === ContactType.Business;
    },
    isTechnicalForm(contactType) {
      return contactType === ContactType.Technical;
    },
    async validate() {
      const results = await Promise.all(this.forms.map((form) => this.$refs[form.contactType][0].validate()));
      return results.every((result) => result);
    },
    qaFormTitle(formTitle) {
      return formTitle.replace(/\s+/g, '');
    }
  }
};
</script>

<style lang="scss" scoped>
.p-contact-wizard-step {
  padding: 24px;

  &__card {
    max-width: 832px;
    margin: auto;
  }
}

.p-contact-wizard-step-card {
  &__description {
    padding: 16px 24px;
  }

  &__form {

  }
}

.p-contact-wizard-step-form {
  padding: 0 24px;

  &__title {

  }

  &__description {
    margin-top: 8px;
  }

  &__content {
    margin-top: 8px;
  }

  &__footer {
    margin: 0 0 16px;
  }
}
</style>
